/**
 * useRafLoop - RAF loop that properly cleans up on unmount and project dispose
 *
 * Runs a callback every animation frame and automatically stops when:
 * 1. The component unmounts
 * 2. The project is about to be disposed (HMR, project switch)
 *
 * Uses a ref to track disposed state so the loop stays stopped even if
 * deps change and the effect re-runs between dispose and unmount.
 */

import { useEffect, useRef } from 'react';
import { webRuntimeApi } from '../../runtime/web';
import { logger } from '@/logging';

/**
 * Hook to run a callback on every animation frame.
 * Automatically stops on unmount or project dispose.
 *
 * @param callback - Function to call each frame. Return false to stop the loop.
 * @param deps - Dependencies that trigger loop restart when changed
 */
export function useRafLoop(
  callback: () => void | false,
  deps: React.DependencyList
): void {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  // Persists across effect re-runs — prevents loop restart after dispose
  const disposedRef = useRef(false);

  // Track disposed state across the component's lifetime
  useEffect(() => {
    disposedRef.current = false;
    const unsub = webRuntimeApi.subscribe.beforeDispose(() => {
      disposedRef.current = true;
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (disposedRef.current) return;

    let rafId: number;
    let stopped = false;

    const loop = () => {
      if (stopped || disposedRef.current) return;
      try {
        const result = callbackRef.current();
        if (result !== false) {
          rafId = requestAnimationFrame(loop);
        }
      } catch (err) {
        // Stale state (e.g. phantom disposed) — stop the loop.
        // The component will unmount or re-render with valid state shortly.
        stopped = true;
      }
    };

    const unsubDispose = webRuntimeApi.subscribe.beforeDispose(() => {
      stopped = true;
      cancelAnimationFrame(rafId);
    });

    rafId = requestAnimationFrame(loop);

    return () => {
      stopped = true;
      cancelAnimationFrame(rafId);
      unsubDispose();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
